import Vue from 'vue';
import VueRouter from 'vue-router';
import { authenticate } from '../guards/authGuard';
import { USER_ROLES } from '../common/constants';

Vue.use(VueRouter);

const routes = [
  {
    path: '/login',
    name: 'login',
    component: () => import('../views/Auth/Login.vue'),
  },
  {
    path: '/forgot-password',
    name: 'forgot-password',
    component: () => import('../views/Auth/ForgotPass.vue'),
  },
  {
    path: '/reset-password',
    name: 'reset-password',
    component: () => import('../views/Auth/ResetPassword.vue'),
  },
  // Dashboard (home)
  {
    path: '/',
    name: 'dashboard',
    component: () => import('../views/Dashboard/Dashboard.vue'),
    meta: { auth: true },
  },
  // Admin — Super Admin only
  {
    path: '/admin/schools',
    name: 'manage-schools',
    component: () => import('../views/Admin/ManageSchools.vue'),
    meta: { auth: true, rolesRequired: [USER_ROLES.SUPER_ADMIN] },
  },
  {
    path: '/admin/schools/add',
    name: 'add-school',
    component: () => import('../views/Admin/PostSchool.vue'),
    meta: { auth: true, rolesRequired: [USER_ROLES.SUPER_ADMIN] },
  },
  {
    path: '/admin/schools/edit/:id',
    name: 'edit-school',
    component: () => import('../views/Admin/PostSchool.vue'),
    meta: { auth: true, rolesRequired: [USER_ROLES.SUPER_ADMIN] },
  },
  {
    path: '/admin/users',
    name: 'manage-users',
    component: () => import('../views/Admin/ManageUsers.vue'),
    meta: { auth: true, rolesRequired: [USER_ROLES.SUPER_ADMIN, USER_ROLES.SCHOOL_OWNER] },
  },
  {
    path: '/admin/users/add',
    name: 'add-user',
    component: () => import('../views/Admin/PostUser.vue'),
    meta: { auth: true, rolesRequired: [USER_ROLES.SUPER_ADMIN, USER_ROLES.SCHOOL_OWNER] },
  },
  {
    path: '/admin/users/edit/:id',
    name: 'edit-user',
    component: () => import('../views/Admin/PostUser.vue'),
    meta: { auth: true, rolesRequired: [USER_ROLES.SUPER_ADMIN, USER_ROLES.SCHOOL_OWNER] },
  },
  // My Account & Security
  {
    path: '/my-account',
    name: 'my-account',
    component: () => import('../views/Account/MyAccount.vue'),
    meta: { auth: true },
  },
  {
    path: '/security',
    name: 'security',
    component: () => import('../views/Account/Security.vue'),
    meta: { auth: true },
  },
  // School Profile — school owner edits own school
  {
    path: '/school-profile',
    name: 'school-profile',
    component: () => import('../views/Admin/PostSchool.vue'),
    meta: { auth: true, rolesRequired: [USER_ROLES.SCHOOL_OWNER] },
  },
  // Integrations (API tokens + sync logs)
  {
    path: '/settings/integrations',
    name: 'integrations',
    component: () => import('../views/Integrations/Integrations.vue'),
    meta: { auth: true, rolesRequired: [USER_ROLES.SUPER_ADMIN, USER_ROLES.SCHOOL_OWNER] },
  },
  // Contacts
  {
    path: '/contacts',
    name: 'contacts-list',
    component: () => import('../views/Contacts/ContactsList.vue'),
    meta: { auth: true },
  },
  {
    path: '/contacts/import',
    name: 'contacts-import',
    component: () => import('../views/Contacts/ImportContacts.vue'),
    meta: { auth: true },
  },
  {
    path: '/contacts/add',
    name: 'add-contact',
    component: () => import('../views/Contacts/PostContact.vue'),
    meta: { auth: true },
  },
  {
    path: '/contacts/edit/:id',
    name: 'edit-contact',
    component: () => import('../views/Contacts/PostContact.vue'),
    meta: { auth: true },
  },
  {
    path: '/contacts/:id',
    name: 'contact-detail',
    component: () => import('../views/Contacts/ContactDetail.vue'),
    meta: { auth: true },
  },
  // Email Templates
  {
    path: '/email-templates',
    name: 'email-templates-list',
    component: () => import('../views/EmailTemplates/TemplatesList.vue'),
    meta: { auth: true },
  },
  {
    path: '/email-templates/create',
    name: 'email-template-create',
    component: () => import('../views/EmailTemplates/TemplateEditor.vue'),
    meta: { auth: true },
  },
  {
    path: '/email-templates/:id/edit',
    name: 'email-template-edit',
    component: () => import('../views/EmailTemplates/TemplateEditor.vue'),
    meta: { auth: true },
  },
  // Campaigns
  {
    path: '/campaigns',
    name: 'campaigns-list',
    component: () => import('../views/Campaigns/CampaignsList.vue'),
    meta: { auth: true },
  },
  {
    path: '/campaigns/create',
    name: 'campaign-create',
    component: () => import('../views/Campaigns/CreateCampaign.vue'),
    meta: { auth: true },
  },
  {
    path: '/campaigns/:id',
    name: 'campaign-detail',
    component: () => import('../views/Campaigns/CampaignDetail.vue'),
    meta: { auth: true },
  },
  // Email Quick Send
  {
    path: '/email/send',
    name: 'email-send',
    component: () => import('../views/EmailQuickSend/SendEmail.vue'),
    meta: { auth: true },
  },
  // Email Logs
  {
    path: '/email-logs',
    name: 'email-logs',
    component: () => import('../views/EmailLogs/EmailLogsList.vue'),
    meta: { auth: true },
  },
  // SMS
  {
    path: '/sms-templates',
    name: 'sms-templates-list',
    component: () => import('../views/SMS/SMSTemplatesList.vue'),
    meta: { auth: true },
  },
  {
    path: '/sms-templates/create',
    name: 'sms-template-create',
    component: () => import('../views/SMS/SMSTemplateEditor.vue'),
    meta: { auth: true },
  },
  {
    path: '/sms-templates/:id/edit',
    name: 'sms-template-edit',
    component: () => import('../views/SMS/SMSTemplateEditor.vue'),
    meta: { auth: true },
  },
  {
    path: '/sms/send',
    name: 'sms-send',
    component: () => import('../views/SMS/SendSMS.vue'),
    meta: { auth: true },
  },
  {
    path: '/sms/log',
    name: 'sms-log',
    component: () => import('../views/SMS/SMSLog.vue'),
    meta: { auth: true },
  },
  {
    path: '/sms-campaigns',
    name: 'sms-campaigns-list',
    component: () => import('../views/SMSCampaigns/SMSCampaignsList.vue'),
    meta: { auth: true },
  },
  {
    path: '/sms-campaigns/create',
    name: 'sms-campaign-create',
    component: () => import('../views/SMSCampaigns/CreateSMSCampaign.vue'),
    meta: { auth: true },
  },
  {
    path: '/sms-campaigns/:id',
    name: 'sms-campaign-detail',
    component: () => import('../views/SMSCampaigns/SMSCampaignDetail.vue'),
    meta: { auth: true },
  },
  // Automations
  {
    path: '/automations',
    name: 'automations-list',
    component: () => import('../views/Automations/AutomationsList.vue'),
    meta: { auth: true },
  },
  {
    path: '/automations/create',
    name: 'automation-create',
    component: () => import('../views/Automations/AutomationEditor.vue'),
    meta: { auth: true },
  },
  {
    path: '/automations/logs',
    name: 'automation-logs',
    component: () => import('../views/Automations/AutomationLogs.vue'),
    meta: { auth: true },
  },
  {
    path: '/automations/:id/edit',
    name: 'automation-edit',
    component: () => import('../views/Automations/AutomationEditor.vue'),
    meta: { auth: true },
  },
  // Surveys
  {
    path: '/surveys',
    name: 'surveys-list',
    component: () => import('../views/Surveys/SurveysList.vue'),
    meta: { auth: true },
  },
  {
    path: '/surveys/create',
    name: 'survey-create',
    component: () => import('../views/Surveys/SurveyEditor.vue'),
    meta: { auth: true },
  },
  {
    path: '/surveys/:id/edit',
    name: 'survey-edit',
    component: () => import('../views/Surveys/SurveyEditor.vue'),
    meta: { auth: true },
  },
  {
    path: '/surveys/:id/responses',
    name: 'survey-responses',
    component: () => import('../views/Surveys/SurveyResponses.vue'),
    meta: { auth: true },
  },
  // Contracts
  {
    path: '/contract-templates',
    name: 'contract-templates-list',
    component: () => import('../views/Contracts/ContractTemplatesList.vue'),
    meta: { auth: true },
  },
  {
    path: '/contract-templates/create',
    name: 'contract-template-create',
    component: () => import('../views/Contracts/ContractTemplateEditor.vue'),
    meta: { auth: true },
  },
  {
    path: '/contract-templates/:id/edit',
    name: 'contract-template-edit',
    component: () => import('../views/Contracts/ContractTemplateEditor.vue'),
    meta: { auth: true },
  },
  {
    path: '/contracts',
    name: 'contracts-list',
    component: () => import('../views/Contracts/ContractsList.vue'),
    meta: { auth: true },
  },
  {
    path: '/contracts/create',
    name: 'contract-create',
    component: () => import('../views/Contracts/CreateContract.vue'),
    meta: { auth: true },
  },
  {
    path: '/contracts/:id',
    name: 'contract-detail',
    component: () => import('../views/Contracts/ContractDetail.vue'),
    meta: { auth: true },
  },
  // Public signing page (no auth required)
  {
    path: '/sign/:token',
    name: 'sign-contract',
    component: () => import('../views/Contracts/SignContract.vue'),
  },
];

export const createRouter = () => {
  const router = new VueRouter({
    mode: 'history',
    routes,
  });

  router.beforeEach((to, from, next) => {
    authenticate(to, from, next);
  });

  return router;
};

const router = createRouter();
export default router;
